import { supabase } from '../config/supabase';

export interface AvailabilityResult {
  isAvailable: boolean;
  conflictingRentals: Array<{
    id: string;
    customer_name: string;
    rental_date: string;
    expected_return_date: string;
    status: string;
  }>;
  nextAvailableDate?: string;
}

export interface AvailabilityPeriod {
  startDate: string;
  endDate: string;
}

/**
 * Check if a dress is available for a specific date range
 */
export const checkDressAvailability = async (
  dressId: string,
  startDate: string,
  endDate: string,
  excludeRentalId?: string
): Promise<AvailabilityResult> => {
  try {
    // Query active rentals that overlap with the requested period
    let query = supabase
      .from('rentals')
      .select(`
        id,
        rental_date,
        expected_return_date,
        status,
        customers (
          name
        )
      `)
      .eq('dress_id', dressId)
      .eq('status', 'active')
      .lte('rental_date', endDate)
      .gte('expected_return_date', startDate);

    // Exclude current rental if editing
    if (excludeRentalId) {
      query = query.neq('id', excludeRentalId);
    }

    const { data: conflictingRentals, error } = await query;

    if (error) throw error;

    console.log('Availability check:', {
      dressId,
      startDate,
      endDate,
      foundRentals: conflictingRentals?.length || 0,
      rawData: conflictingRentals
    });

    const conflicts = (conflictingRentals || []).map(rental => ({
      id: rental.id,
      customer_name: (rental as any).customers?.name || 'Unknown',
      rental_date: rental.rental_date,
      expected_return_date: rental.expected_return_date,
      status: rental.status
    }));

    const isAvailable = conflicts.length === 0;

    // Find next available date if not available
    let nextAvailableDate: string | undefined;
    if (!isAvailable) {
      // Get all active rentals for this dress
      const { data: allRentals } = await supabase
        .from('rentals')
        .select('rental_date, expected_return_date')
        .eq('dress_id', dressId)
        .eq('status', 'active')
        .order('expected_return_date', { ascending: true });

      if (allRentals && allRentals.length > 0) {
        // Find the earliest date after all current rentals
        const latestReturn = allRentals
          .map(r => new Date(r.expected_return_date))
          .sort((a, b) => b.getTime() - a.getTime())[0];
        
        const nextDay = new Date(latestReturn);
        nextDay.setDate(nextDay.getDate() + 1);
        nextAvailableDate = nextDay.toISOString().split('T')[0];
      }
    }

    return {
      isAvailable,
      conflictingRentals: conflicts,
      nextAvailableDate
    };
  } catch (error) {
    console.error('Error checking dress availability:', error);
    return {
      isAvailable: false,
      conflictingRentals: []
    };
  }
};

/**
 * Get availability status for a dress (current moment)
 */
export const getCurrentDressAvailability = async (dressId: string): Promise<{
  isCurrentlyAvailable: boolean;
  currentRental?: {
    customer_name: string;
    expected_return_date: string;
    days_until_return: number;
  };
}> => {
  try {
    const today = new Date().toISOString().split('T')[0];
    
    const { data: currentRental, error } = await supabase
      .from('rentals')
      .select(`
        expected_return_date,
        customers (
          name
        )
      `)
      .eq('dress_id', dressId)
      .eq('status', 'active')
      .lte('rental_date', today)
      .gte('expected_return_date', today)
      .single();

    if (error && error.code !== 'PGRST116') throw error;

    if (currentRental) {
      const returnDate = new Date(currentRental.expected_return_date);
      const todayDate = new Date(today);
      const daysUntilReturn = Math.ceil((returnDate.getTime() - todayDate.getTime()) / (1000 * 60 * 60 * 24));

      return {
        isCurrentlyAvailable: false,
        currentRental: {
          customer_name: (currentRental as any).customers?.name || 'Unknown',
          expected_return_date: currentRental.expected_return_date,
          days_until_return: daysUntilReturn
        }
      };
    }

    return {
      isCurrentlyAvailable: true
    };
  } catch (error) {
    console.error('Error checking current dress availability:', error);
    return {
      isCurrentlyAvailable: false
    };
  }
};

/**
 * Get upcoming returns for calendar view
 */
export const getUpcomingReturns = async (startDate: string, endDate: string) => {
  try {
    const { data: returns, error } = await supabase
      .from('rentals')
      .select(`
        id,
        expected_return_date,
        rental_date,
        customers (
          id,
          name,
          email
        ),
        dresses (
          id,
          name,
          barcode,
          photo_url
        )
      `)
      .eq('status', 'active')
      .gte('expected_return_date', startDate)
      .lte('expected_return_date', endDate)
      .order('expected_return_date', { ascending: true });

    if (error) throw error;
    return returns || [];
  } catch (error) {
    console.error('Error fetching upcoming returns:', error);
    return [];
  }
}; 